import React, { useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  useFetchEmailTemplateHistoryQuery,
  useFetchPushTemplateHistoryQuery,
} from '../../redux/features/notificationTemplate/notificationTemplate.apiSlice';
import {
  EmailTemplateSnapshot,
  NotificationTemplateHistoryEntry,
  PushTemplateSnapshot,
  TemplateChannel,
} from '../../types/notificationTemplate';
import { formatDateTime } from '../../utils/date';

const PUSH_LABELS: Record<keyof PushTemplateSnapshot, string> = {
  key: 'Key',
  titleAr: 'Title (Arabic)',
  titleEn: 'Title (English)',
  bodyAr: 'Body (Arabic)',
  bodyEn: 'Body (English)',
  category: 'Category',
  isActive: 'Active',
};

const EMAIL_LABELS: Record<keyof EmailTemplateSnapshot, string> = {
  key: 'Key',
  sendgridTemplateId: 'SendGrid template id',
  description: 'Description',
  category: 'Category',
  isActive: 'Active',
};

type Snapshot = Partial<Record<string, string | boolean | null>>;

const display = (field: string, value: string | boolean | null | undefined) => {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (value) {
    return value;
  }

  return field.startsWith('title') ? '(empty: shows مدخول / Madkhol)' : '(empty)';
};

interface NotificationTemplateHistoryProps {
  channel: TemplateChannel;
  templateId: number;
}

/** Who changed the template, when, and what each change replaced. Loaded when first expanded. */
export function NotificationTemplateHistory({ channel, templateId }: NotificationTemplateHistoryProps) {
  const [expanded, setExpanded] = useState(false);
  const push = useFetchPushTemplateHistoryQuery(templateId, { skip: !expanded || channel !== 'PUSH' });
  const email = useFetchEmailTemplateHistoryQuery(templateId, { skip: !expanded || channel !== 'EMAIL' });
  const { data, isFetching, isError } = channel === 'PUSH' ? push : email;
  const entries = data as NotificationTemplateHistoryEntry<Snapshot>[] | undefined;
  const labels: Record<string, string> = channel === 'PUSH' ? PUSH_LABELS : EMAIL_LABELS;

  return (
    <Accordion expanded={expanded} onChange={(event, isExpanded) => setExpanded(isExpanded)} sx={{ mt: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>Change history</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {isFetching && <CircularProgress size={24} />}
        {isError && <Alert severity="error">Could not load the history.</Alert>}
        {!isFetching && !isError && !entries?.length && (
          <Typography color="text.secondary">No changes recorded for this template.</Typography>
        )}
        {!isFetching &&
          entries?.map((entry, index) => {
            const created = entry.previousValue === null;
            const fields = created
              ? []
              : Object.keys(labels).filter((field) => entry.previousValue?.[field] !== entry.newValue?.[field]);

            return (
              <Box key={entry.id}>
                {index > 0 && <Divider sx={{ my: 2 }} />}
                <Typography variant="subtitle2">
                  {formatDateTime(entry.createdAt)} by {entry.employeeName || `employee #${entry.employeeId}`}
                </Typography>
                {created && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Created, {entry.newValue.isActive ? 'active' : 'inactive'}.
                  </Typography>
                )}
                {fields.map((field) => (
                  <Box key={field} sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {labels[field]}
                    </Typography>
                    <Box
                      dir={field.endsWith('Ar') ? 'rtl' : 'ltr'}
                      sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1, whiteSpace: 'pre-wrap' }}
                    >
                      <Typography variant="body2" color="error.main">
                        −
                      </Typography>
                      <Typography variant="body2" sx={{ textDecoration: 'line-through' }} color="text.secondary">
                        {display(field, entry.previousValue?.[field])}
                      </Typography>
                      <Typography variant="body2" color="success.main">
                        +
                      </Typography>
                      <Typography variant="body2">{display(field, entry.newValue?.[field])}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            );
          })}
      </AccordionDetails>
    </Accordion>
  );
}
